import { Apple, Copy, Moon, Plus, Sparkles, Sun, Sunrise } from 'lucide-react';
import type { Entry, MealView, UsualMeal } from '../../shared/api';
import { AI_SOURCES } from '../../shared/sources';
import { MEAL_LABELS, type Meal } from '../../shared/dates';
import { scaleMap } from '../../shared/nutrients';
import { kcal } from '../lib/format';

const ICONS: Record<Meal, typeof Sun> = { breakfast: Sunrise, lunch: Sun, dinner: Moon, snack: Apple };

interface Props {
  view: MealView;
  yesterdayCount: number;
  usual?: UsualMeal;
  onAdd: () => void;
  onOpenEntry: (entry: Entry) => void;
  onCopyYesterday: () => void;
  onLogUsual: (usual: UsualMeal) => void;
}

export function MealSection({ view, yesterdayCount, usual, onAdd, onOpenEntry, onCopyYesterday, onLogUsual }: Props) {
  const Icon = ICONS[view.meal];
  const empty = view.entries.length === 0;
  return (
    <section className="meal" aria-label={MEAL_LABELS[view.meal]}>
      <div className="meal__head">
        <span className={`meal__icon meal__icon--${view.meal}`} aria-hidden="true">
          <Icon size={18} />
        </span>
        <div className="grow">
          <div className="meal__title">{MEAL_LABELS[view.meal]}</div>
          {!empty && <div className="meal__kcal num">{kcal(view.totals.kcal)} kcal · {Math.round(view.totals.protein ?? 0)} g protein</div>}
        </div>
        <button className="btn btn--ghost btn--icon btn--sm" aria-label={`Add to ${MEAL_LABELS[view.meal]}`} onClick={onAdd}>
          <Plus size={18} />
        </button>
      </div>

      {view.entries.map((e) => {
        const n = scaleMap(e.per100, e.grams);
        return (
          <button key={e.id} className="entry" onClick={() => onOpenEntry(e)}>
            <div className="grow">
              <div className="entry__name">{e.name}</div>
              <div className="entry__meta num">
                {Math.round(e.grams)} g{e.brand ? ` · ${e.brand}` : ''}
                {AI_SOURCES.has(e.source) && (
                  <span className="badge badge--ai" style={{ marginLeft: 6 }}>
                    <Sparkles size={10} /> AI
                  </span>
                )}
              </div>
            </div>
            <span className="entry__kcal num">{kcal(n.kcal)}</span>
          </button>
        );
      })}

      {empty && (usual || yesterdayCount > 0) && (
        <div className="meal__empty">
          {usual && (
            <button className="chip chip--accent" onClick={() => onLogUsual(usual)} title={usual.items.map((i) => i.name).join(', ')}>
              <Plus size={14} /> {usual.title} · {kcal(usual.totals.kcal)} kcal
            </button>
          )}
          {yesterdayCount > 0 && (
            <button className="chip" onClick={onCopyYesterday}>
              <Copy size={14} /> Copy yesterday's {MEAL_LABELS[view.meal].toLowerCase()}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
